// Page modules, lazy-loaded so the initial JS graph holds only login +
// shell + auth + react-query; each route's chunk loads on first visit.
// Modules export named functions (no default), so each import adapts to
// React.lazy's default-export contract inline.
//
// `page(<X />)` wraps a lazy page in the shared Suspense fallback, inside
// an error boundary that clears when the path changes (RouteBoundary).

import { Suspense, lazy, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { ErrorBoundary } from "@/components/ui/ErrorBoundary";

import { isEmbedded } from "@/native/embedded";
import { EmbeddedPending } from "@/native/renderReady";

export const OrgChartTab = lazy(() => import("@/pages/customers/OrgChartTab").then((m) => ({ default: m.OrgChartTab })));
export const AgentsTab = lazy(() => import("@/pages/customers/AgentsTab").then((m) => ({ default: m.AgentsTab })));
export const AgentWorkspaceTab = lazy(() =>
  import("@/pages/customers/AgentWorkspaceTab").then((m) => ({ default: m.AgentWorkspaceTab })),
);
export const CCSessionsTab = lazy(() => import("@/pages/customers/CCSessionsTab").then((m) => ({ default: m.CCSessionsTab })));
export const ConnectedSessionsTab = lazy(() =>
  import("@/pages/customers/ConnectedSessionsTab").then((m) => ({ default: m.ConnectedSessionsTab })),
);
export const CCDeploysTab = lazy(() => import("@/pages/customers/CCDeploysTab").then((m) => ({ default: m.CCDeploysTab })));
export const CodeTab = lazy(() => import("@/pages/customers/CodeTab").then((m) => ({ default: m.CodeTab })));
export const MeetingsTab = lazy(() => import("@/pages/customers/MeetingsTab").then((m) => ({ default: m.MeetingsTab })));
export const RecordTab = lazy(() => import("@/pages/customers/RecordTab").then((m) => ({ default: m.RecordTab })));
export const ProjectsTab = lazy(() => import("@/pages/customers/ProjectsTab").then((m) => ({ default: m.ProjectsTab })));
export const DeliverablesPage = lazy(() =>
  import("@/features/deliverables/DeliverablesPage").then((m) => ({ default: m.DeliverablesPage })),
);
export const SharedTab = lazy(() => import("@/pages/customers/SharedTab").then((m) => ({ default: m.SharedTab })));
export const SkillsTab = lazy(() => import("@/pages/customers/SkillsTab").then((m) => ({ default: m.SkillsTab })));
export const SubagentsTab = lazy(() => import("@/pages/customers/SubagentsTab").then((m) => ({ default: m.SubagentsTab })));
export const SubagentDesignerPage = lazy(() =>
  import("@/pages/customers/SubagentDesignerPage").then((m) => ({ default: m.SubagentDesignerPage })),
);
export const SubagentBlueprintPage = lazy(() =>
  import("@/pages/customers/SubagentBlueprintPage").then((m) => ({ default: m.SubagentBlueprintPage })),
);
export const SettingsTab = lazy(() => import("@/pages/customers/SettingsTab").then((m) => ({ default: m.SettingsTab })));
export const MachineApprovalPage = lazy(() =>
  import("@/pages/customers/MachineApprovalPage").then((m) => ({ default: m.MachineApprovalPage })),
);
export const WorkflowsTab = lazy(() => import("@/pages/customers/WorkflowsTab").then((m) => ({ default: m.WorkflowsTab })));
export const SchedulesTab = lazy(() => import("@/pages/customers/SchedulesTab").then((m) => ({ default: m.SchedulesTab })));
export const WorkflowEditorPage = lazy(() =>
  import("@/pages/customers/WorkflowEditorPage").then((m) => ({ default: m.WorkflowEditorPage })),
);
export const DataSourcePage = lazy(() => import("@/pages/customers/DataSourcePage").then((m) => ({ default: m.DataSourcePage })));
export const ConnectionExplorePage = lazy(() => import("@/pages/customers/ConnectionExplorePage").then((m) => ({ default: m.ConnectionExplorePage })));
export const DataSourcesPage = lazy(() => import("@/pages/customers/DataSourcesPage").then((m) => ({ default: m.DataSourcesPage })));
export const SavedResultsPage = lazy(() => import("@/pages/customers/SavedResultsPage").then((m) => ({ default: m.SavedResultsPage })));
export const SavedResultPage = lazy(() => import("@/pages/customers/SavedResultPage").then((m) => ({ default: m.SavedResultPage })));
// Published, sent to Deployments once Deploy from words replaces it (P6);
// the Deployments pages, sent to Published where the flag is off
// (features/workflows/deployments/deploy/gates, loaded with the page).
const gates = () => import("@/features/workflows/deployments/deploy/gates");
export const PublishedPage = lazy(() =>
  Promise.all([import("@/pages/customers/PublishedPage"), gates()]).then(([m, g]) => ({ default: g.gated(m.PublishedPage, "published") })),
);
export const DeployPage = lazy(() => import("@/pages/customers/DeployPage").then((m) => ({ default: m.DeployPage })));
// The Deployments list and one deployment, each behind its gate.
export const DeploymentsPage = lazy(() =>
  Promise.all([import("@/pages/customers/DeploymentsPage"), gates()]).then(([m, g]) => ({ default: g.gated(m.DeploymentsPage, "deployments") })),
);
export const DeploymentPage = lazy(() =>
  Promise.all([import("@/pages/customers/DeploymentPage"), gates()]).then(([m, g]) => ({ default: g.gated(m.DeploymentPage, "deployments") })),
);
export const WorkflowRunPage = lazy(() =>
  import("@/pages/customers/WorkflowRunPage").then((m) => ({ default: m.WorkflowRunPage })),
);
export const NewJobPage = lazy(() => import("@/pages/customers/NewJobPage").then((m) => ({ default: m.NewJobPage })));
export const JobPage = lazy(() => import("@/pages/customers/JobPage").then((m) => ({ default: m.JobPage })));
export const TodosTab = lazy(() => import("@/pages/customers/TodosTab").then((m) => ({ default: m.TodosTab })));
export const TasksTab = lazy(() => import("@/pages/customers/TasksTab").then((m) => ({ default: m.TasksTab })));
export const UiGuidePage = lazy(() => import("@/pages/UiGuidePage").then((m) => ({ default: m.UiGuidePage })));
export const AdminApp = lazy(() => import("@/pages/admin/AdminApp").then((m) => ({ default: m.AdminApp })));

// Companion (phone) screens.
export const TodayScreen = lazy(() => import("@/companion/TodayScreen").then((m) => ({ default: m.TodayScreen })));
export const ChatScreen = lazy(() => import("@/companion/ChatScreen").then((m) => ({ default: m.ChatScreen })));
export const ChatThread = lazy(() => import("@/companion/ChatThread").then((m) => ({ default: m.ChatThread })));
export const CompanionMemory = lazy(() => import("@/features/memories/MemoryPanel").then((m) => ({ default: m.CompanionMemory })));
export const MoreScreen = lazy(() => import("@/companion/MoreScreen").then((m) => ({ default: m.MoreScreen })));
export const CompanionRecord = lazy(() => import("@/companion/screens").then((m) => ({ default: m.RecordScreen })));
export const CompanionMeetings = lazy(() =>
  import("@/companion/CompanionMeetings").then((m) => ({ default: m.CompanionMeetings })),
);
export const DeliverableViewer = lazy(() =>
  import("@/companion/DeliverableViewer").then((m) => ({ default: m.DeliverableViewer })),
);
export const FamilyChat = lazy(() => import("@/features/family-user/FamilyChat"));

/** Layout-neutral placeholder while a route chunk loads (tens of ms). */
export function RouteLoading() {
  // Inside the iPhone app the app draws its own placeholder until the page
  // has drawn (src/native/renderReady.tsx): no "Loading…" there.
  if (isEmbedded()) return <EmbeddedPending />;
  return (
    <div className="flex h-full w-full items-center justify-center" role="status">
      <span className="text-sm text-fg-subtle">Loading…</span>
    </div>
  );
}

/** A crash in a page shows a way out; navigating elsewhere clears it. */
function RouteBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

export function page(element: ReactNode) {
  return (
    <RouteBoundary>
      <Suspense fallback={<RouteLoading />}>{element}</Suspense>
    </RouteBoundary>
  );
}
